import { useState, useEffect, useCallback, useMemo } from 'react'
import { Container, Row, Col, Card, Alert, Badge, Tabs, Tab } from 'react-bootstrap'
import { useAppStore } from '../stores/appStore'
import VirtualLogViewer from '../components/VirtualLogViewer'
import TaskSelector from '../components/TaskSelector'
import TaskConfigPanel from '../components/TaskConfigPanel'
import AccountSelector from '../components/AccountSelector'
import ExecutionController from '../components/ExecutionController'
import BatchConfigIO from '../components/BatchConfigIO'
import GroupSelector from '../components/GroupSelector'
import { useTaskRuntime } from '../contexts/TaskRuntimeContext'

const DailyTasks = () => {
  const {
    taskManager,
    updateGlobalTaskConfig,
    getGlobalTaskConfig,
    getRuntimeData,
    getRuntimeSelectedTaskIds,
    setRuntimeSelectedTaskIds,
    config,
    updateConfig,
  } = useAppStore()
  const {
    logs,
    addLog,
    clearLogs,
    exportLogs,
    getLogStats,
  } = useTaskRuntime()
  const [selectedAccounts, setSelectedAccounts] = useState([])
  const [taskConfigs, setTaskConfigs] = useState({})
  const [executionMode, setExecutionMode] = useState('once') // 'once' | 'scheduled'
  const [scheduleTime, setScheduleTime] = useState('08:00')
  const [browserRequestInterval, setBrowserRequestInterval] = useState(350)
  const [isExecuting, setIsExecuting] = useState(false)
  const runtimeData = getRuntimeData()
  const selectedTaskIds = getRuntimeSelectedTaskIds()
  const runtimeAccounts = useMemo(() => runtimeData.accounts || {}, [runtimeData.accounts])
  const runtimeAccountKeys = useMemo(() => Object.keys(runtimeAccounts), [runtimeAccounts])

  const logAccountOptions = useMemo(() => {
    return Object.entries(runtimeAccounts).map(([accountKey, account]) => {
      const serverText = account?.server ? ` (${account.server})` : ''
      return {
        value: accountKey,
        label: `${account?.username || accountKey}${serverText}`
      }
    })
  }, [runtimeAccounts])

  const logTaskOptions = useMemo(() => {
    if (!taskManager) return []
    return taskManager.getAllTasks().map(task => ({
      value: task.id,
      label: task.name
    }))
  }, [taskManager])

  const sanitizeSelectedAccounts = useCallback((accountKeys = []) => {
    const availableAccounts = new Set(runtimeAccountKeys)
    return accountKeys.filter(accountKey => availableAccounts.has(accountKey))
  }, [runtimeAccountKeys])

  const handleAccountSelectionChange = useCallback((accountKeys) => {
    setSelectedAccounts(sanitizeSelectedAccounts(accountKeys))
  }, [sanitizeSelectedAccounts])

  useEffect(() => {
    setSelectedAccounts(prevSelectedAccounts => {
      const nextSelectedAccounts = sanitizeSelectedAccounts(prevSelectedAccounts)
      return nextSelectedAccounts.length === prevSelectedAccounts.length
        ? prevSelectedAccounts
        : nextSelectedAccounts
    })
  }, [sanitizeSelectedAccounts])

  const runtimeSelectedAccounts = useMemo(
    () => sanitizeSelectedAccounts(selectedAccounts),
    [selectedAccounts, sanitizeSelectedAccounts]
  )

  const getRuntimeTaskConfig = useCallback((taskId) => {
    return getGlobalTaskConfig(taskId)
  }, [getGlobalTaskConfig])

  const updateRuntimeTaskConfig = useCallback((taskId, config) => {
    updateGlobalTaskConfig(taskId, config)
  }, [updateGlobalTaskConfig])

  // 当选中的任务改变时，同步全局配置到本地状态
  useEffect(() => {
    if (selectedTaskIds.length > 0) {
      const syncedConfigs = {}
      selectedTaskIds.forEach(taskId => {
        const globalConfig = getRuntimeTaskConfig(taskId)
        if (globalConfig && Object.keys(globalConfig).length > 0) {
          syncedConfigs[taskId] = globalConfig
        }
      })
      
      // 只有当配置有变化时才更新
      if (Object.keys(syncedConfigs).length > 0) {
        setTaskConfigs(prevConfigs => ({
          ...prevConfigs,
          ...syncedConfigs
        }))
      }
    }
  }, [selectedTaskIds, getRuntimeTaskConfig])

  // 处理加载定时任务配置
  const handleLoadJobConfig = (jobConfig) => {
    console.log('DailyTasks 接收到配置:', jobConfig)
    
    // 深拷贝配置以确保状态正确更新
    const configsCopy = JSON.parse(JSON.stringify(jobConfig.configs || {}))
    
    console.log('设置任务配置:', configsCopy)
    
    // 更新任务和账号选择
    setRuntimeSelectedTaskIds([...(jobConfig.tasks || [])])
    handleAccountSelectionChange(jobConfig.accounts || [])
    setTaskConfigs(configsCopy)
    if (jobConfig.requestInterval !== undefined) {
      setBrowserRequestInterval(jobConfig.requestInterval)
    }
    
    // 重要：同时更新全局配置，这样 TaskConfigPanel 才能正确显示
    Object.entries(configsCopy).forEach(([taskId, config]) => {
      console.log(`更新全局配置 - 任务: ${taskId}, 配置:`, config)
      updateRuntimeTaskConfig(taskId, config)
    })
    
    addLog(`定时任务配置已加载: ${jobConfig.tasks.length}个任务, ${jobConfig.accounts.length}个账号`, 'success')
  }

  // 活跃度闸门开关（默认开启）
  const activitySkipEnabled = config?.activitySkipEnabled !== false
  const dailyRuntimeResetEnabled = config?.dailyRuntimeResetEnabled !== false

  // 应用导入的批量任务配置
  const handleApplyBatchConfig = (data) => {
    try {
      // 任务只保留本机已注册的任务
      const validTasks = (data.tasks || []).filter((taskId) => taskManager?.getTask(taskId))
      const invalidTaskCount = (data.tasks || []).length - validTasks.length

      // 账号只保留本机存在的账号，缺失的跳过并计数
      const validAccounts = (data.accounts || []).filter((accountKey) => runtimeAccounts[accountKey])
      const skippedAccountCount = (data.accounts || []).length - validAccounts.length

      // 应用任务选择
      setRuntimeSelectedTaskIds(validTasks)

      // 应用账号选择
      handleAccountSelectionChange(validAccounts)

      // 应用各任务配置（仅本机存在的任务）
      const appliedConfigs = {}
      Object.entries(data.configs || {}).forEach(([taskId, cfg]) => {
        if (!taskManager?.getTask(taskId)) return
        appliedConfigs[taskId] = cfg
        updateRuntimeTaskConfig(taskId, cfg)
      })
      setTaskConfigs(appliedConfigs)

      // 应用请求间隔
      if (typeof data.requestInterval === 'number') {
        setBrowserRequestInterval(data.requestInterval)
      }

      let summary = `批量任务配置导入完成: ${validTasks.length} 个任务, ${validAccounts.length} 个账号`
      const notes = []
      if (invalidTaskCount > 0) notes.push(`跳过无效任务 ${invalidTaskCount} 个`)
      if (skippedAccountCount > 0) notes.push(`跳过缺失账号 ${skippedAccountCount} 个`)
      if (notes.length) summary += `（${notes.join('，')}）`
      addLog(summary, 'success')
    } catch (error) {
      addLog(`导入批量任务配置失败: ${error.message}`, 'error')
    }
  }

  // 检查是否有账号
  const hasAccounts = runtimeAccountKeys.length > 0
  const handleClearLogs = () => {
    clearLogs()
  }

  if (!hasAccounts) {
    return (
      <Container fluid>
        <Row className="mb-4">
          <Col xs={12}>
            <Alert variant="warning" className="text-center">
              <h5>请先添加账号</h5>
              <p>您需要在账号管理页面添加账号后才能使用日常任务功能。</p>
            </Alert>
          </Col>
        </Row>
      </Container>
    )
  }

  return (
    <Container fluid>
      {/* 页面标题 */}
      <Row className="mb-4">
        <Col xs={12}>
          <Card>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h4 className="card-title text-primary mb-1">日常任务管理</h4>
                  <p className="card-text text-muted mb-0">选择任务 → 配置参数 → 选择账号 → 执行任务</p>
                </div>
                <div className="d-flex gap-2">
                  <Badge bg="info">{selectedTaskIds.length} 个任务</Badge>
                  <Badge bg="success">{runtimeSelectedAccounts.length} 个账号</Badge>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        {/* 左侧：任务选择和配置 */}
        <Col lg={8}>
          {/* 组选择器 */}
          <GroupSelector
            selectedTaskIds={selectedTaskIds}
            onTaskSelectionChange={setRuntimeSelectedTaskIds}
            selectedAccounts={runtimeSelectedAccounts}
            onAccountSelectionChange={handleAccountSelectionChange}
            taskConfigs={taskConfigs}
            onConfigChange={setTaskConfigs}
            taskManager={taskManager}
            updateConfigOverride={updateRuntimeTaskConfig}
          />
          
          <Tabs defaultActiveKey="task-selection" className="mb-3">
            <Tab eventKey="task-selection" title="任务选择">
              <TaskSelector
                selectedTasks={selectedTaskIds}
                onTaskSelectionChange={setRuntimeSelectedTaskIds}
                taskManager={taskManager}
              />
            </Tab>
            <Tab eventKey="task-config" title="任务配置">
              <TaskConfigPanel
                selectedTasks={selectedTaskIds}
                selectedAccounts={runtimeSelectedAccounts}
                availableAccounts={runtimeAccounts}
                taskConfigs={taskConfigs}
                onConfigChange={setTaskConfigs}
                taskManager={taskManager}
                getConfigOverride={getRuntimeTaskConfig}
                updateConfigOverride={updateRuntimeTaskConfig}
              />
            </Tab>
          </Tabs>
        </Col>

        {/* 右侧：账号选择和执行控制 */}
        <Col lg={4}>
          <Card className="mb-3">
            <Card.Header>
              <h6 className="mb-0">账号选择</h6>
            </Card.Header>
            <Card.Body>
              <AccountSelector
                selectedAccounts={runtimeSelectedAccounts}
                onAccountSelectionChange={handleAccountSelectionChange}
                accounts={runtimeAccounts}
              />
            </Card.Body>
          </Card>

          <Card className="mb-3">
            <Card.Header>
              <h6 className="mb-0">执行控制</h6>
            </Card.Header>
            <Card.Body>
              <ExecutionController
                selectedTasks={selectedTaskIds}
                selectedAccounts={runtimeSelectedAccounts}
                taskConfigs={taskConfigs}
                executionMode={executionMode}
                scheduleTime={scheduleTime}
                requestInterval={browserRequestInterval}
                isExecuting={isExecuting}
                onExecutionModeChange={setExecutionMode}
                onScheduleTimeChange={setScheduleTime}
                onRequestIntervalChange={setBrowserRequestInterval}
                activitySkipEnabled={activitySkipEnabled}
                onActivitySkipEnabledChange={(v) => updateConfig({ activitySkipEnabled: v })}
                dailyRuntimeResetEnabled={dailyRuntimeResetEnabled}
                onDailyRuntimeResetEnabledChange={(v) => updateConfig({ dailyRuntimeResetEnabled: v })}
                onExecute={setIsExecuting}
                taskManager={taskManager}
                addLog={addLog}
                onLoadJobConfig={handleLoadJobConfig}
              />
            </Card.Body>
          </Card>

          <BatchConfigIO
            selectedTasks={selectedTaskIds}
            selectedAccounts={runtimeSelectedAccounts}
            taskConfigs={taskConfigs}
            requestInterval={browserRequestInterval}
            disabled={isExecuting}
            onImport={handleApplyBatchConfig}
            addLog={addLog}
          />
        </Col>
      </Row>

      {/* 日志区域 */}
      <VirtualLogViewer 
        logs={logs} 
        onClearLogs={handleClearLogs}
        exportLogs={exportLogs}
        getLogStats={getLogStats}
        accountOptions={logAccountOptions}
        taskOptions={logTaskOptions}
      />
    </Container>
  )
}

export default DailyTasks
